<template>
  <section id="slider">
    <ul class="slider__text-cont"
      :style="{
        height: `${windowH}px`,
        transform: `rotate(${rotation.val}deg) translate3d(0, 0, ${scale}px)`
      }"
      ref="silderCont"
    >
      <li
        class="slider__item"
        v-for="text in texts"
        :key="text.title"
        :style="{
          top: `${text.top}px`,
          left: `${text.left}px`,
          transform: `translate3d(${scrollValue}px, 0, 0)`
        }"
        @click="clickVoice"
      >
        <router-link
          :to="text.id"
          :data-hover="text.title"
          :data-state="text.state"
          data-mouse
          data-mouse-attractionState="disactive"
          data-hover-type="fadeout"
          :style="{
            paddingRight: `${paddingRight}px`,
          }"
        >
          {{text.title}}
        </router-link>
      </li>
    </ul>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import gsap from 'gsap';
import loop from 'raf-loop';
import VirtualScroll from 'virtual-scroll';
import Hammer from 'hammerjs';
import FontFaceObserver from 'fontfaceobserver';

import { isMobile } from '@/scripts/utils';


export default {
  name: 'Slider',

  props: ['cities'],

  computed: {
    ...mapGetters(['getCity']),
  },

  data() {
    return {
      scrollTarget: null,
      direction: 'left',
      scale: 0,
      scrollValue: 0,
      paddingRight: 45,
      oldScrollValue: 0,
      totalWidth: 0,
      windowH: 0,
      rotation: {
        val: -6,
      },
      texts: [],
      lerp: 0.08,
      isMobile,
      handleKey: null,
    };
  },

  created() {
    this.cities.forEach((city) => {
      this.texts.push({
        title: city.title,
        id: city.id,
        state: city.state,
        scrollValue: 0,
        top: 0,
        left: 0,
        width: 0,
      });
    });

    this.paddingRight = this.isMobile() ? 30 : 60;

    this.mobileFirstCheck = this.isMobile();
  },

  mounted() {
    this.$store.commit('ADD_BUTTONS', this.$el);

    const font = new FontFaceObserver('Neutral', {
      weight: 500,
    });

    this.vs = new VirtualScroll({
      keyStep: -300,
      limitInertia: false,
      passive: true,
    });

    this.hammer = new Hammer(this.$el);
    this.arrow = document.querySelector('.arrow-scroll');
    this.hammerArrow = new Hammer(this.arrow);

    this.setSlider();

    font.load().then(() => {
      this.resize();
      this.addEvents();

      this.animateInSlider();
    }, () => {
      this.resize();
      this.addEvents();

      this.animateInSlider();
    });
  },

  methods: {
    resize() {
      this.contW = this.$el.clientWidth;

      this.scrollTarget = null;
      this.oldScrollValue = 0;
      this.scrollValue = 0;

      this.setTop();
      this.setPos();

      this.update();
    },

    addEvents() {
      if (!this.isMobile()) {
        this.vs.on(this.onScroll, this);
      } else {
        this.hammer.on('swipe', this.onScroll.bind(this));
        this.hammer.get('pinch').set({ enable: true });

        this.handleScroll = () => {
          const a = {
            direction: 2,
            deltaX: -10,
            velocity: 5,
          };

          this.onScroll(a);
        };
        this.hammerArrow.on('tap', this.handleScroll);
      }
      this.engine = loop(this.update.bind(this));
      this.engine.start();

      window.addEventListener('resize', this.resize.bind(this));
    },

    removeEvents() {
      if (!this.isMobile()) {
        this.vs.off(this.onScroll);
      } else {
        this.hammer.off('swipe', this.onScroll.bind(this));
        this.hammer.get('pinch').set({ enable: false });
        this.hammerArrow.off('tap', this.handleScroll);
      }
      this.engine.stop();
      this.engine = null;

      window.removeEventListener('resize', this.resize.bind(this));
    },

    setTop() {
      this.windowH = window.innerHeight;

      this.texts.forEach((el) => {
        const text = el;

        text.el = this.$el.querySelector(`[data-hover="${text.title}"]`).parentElement;
        const textH = text.el.clientHeight;
        const top = Math.round((this.windowH / 2) - (textH / 1.5));

        text.top = top;
      });
    },

    setPos() {
      let left = 0;
      this.totalWidth = 0;

      this.texts.forEach((el) => {
        const text = el;

        text.left = left;
        text.width = text.el.clientWidth;

        left += text.width;

        this.totalWidth += text.width;
      });
    },

    onScroll(e) {
      if (!this.mobileFirstCheck) {
        this.direction = this.oldScrollValue > this.scrollValue ? 'left' : 'right';
      } else {
        this.direction = e.direction === 2 ? 'left' : 'right';
      }

      if (!this.mobileFirstCheck) this.scrollTarget += ((-e.deltaY) || (e.deltaX * 5));
      if (this.mobileFirstCheck) this.scrollTarget += (e.deltaX * Math.abs(e.velocity * 4));
    },

    update() {
      this.oldScrollValue = this.scrollValue;
      this.scrollValue += (this.scrollTarget - this.scrollValue) * this.lerp;

      if (this.oldScrollValue === this.scrollValue) return;

      const sign = this.direction === 'left' ? -1 : 1;
      this.scale = sign * Math.min(Math.abs(Math.round(
        (this.scrollValue - this.oldScrollValue) / 2)), 80);

      this.texts.forEach((el) => {
        const text = el;
        this.checkInfinite(text);
      });
    },

    checkInfinite(el) {
      const text = el;

      const condition = this.direction === 'left' ? (text.left + this.scrollValue + text.width) < 0 :
        (text.left + this.scrollValue) > this.contW;

      const calc = this.direction === 'left' ? text.left + this.totalWidth :
        text.left - this.totalWidth;

      text.left = condition ? calc : text.left;
    },

    setSlider() {
      gsap.set(this.$el, { autoAlpha: 0 });
    },

    animateInSlider() {
      const tl = new gsap.TimelineLite();

      tl
        .to(this, 0.1, {
          scrollValue: '+=300',
          ease: gsap.Expo.easeOut,
        })
        .add(() => {
          this.setPos();
        })
        .to(this.$el, 0.5, { autoAlpha: 1 });
    },

    clickVoice() {
      this.$el.classList.add('no-pointer');
    },
  },

  beforeDestroy() {
    this.removeEvents();
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/colors';
@import '../application';

.slider__centerize {
  position: fixed;
  top: 55%;
  left: 0;
  width: 100%;
  height: auto;
  padding: 0 40px;
  box-sizing: border-box;

  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

#slider {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  perspective: 300px;

  overflow: hidden;

  &.no-pointer {
    pointer-events: none;
  }
}

.slider__text-cont {
  position: relative;
  white-space: nowrap;
  list-style: none;

  .slider__item {
    position: absolute;
    white-space: nowrap;
    display: block;
  }

  a {
    display: inline-block;
    position: relative;
    padding-right: 30px;

    font-size: 4rem;
    font-weight: 500;
    -webkit-text-stroke: 2px $color-blue;
    color: transparent;
    letter-spacing: 2px;
    text-decoration: none;

    @include mq(sm) {
      font-size: 6.875rem;
      letter-spacing: 3px;
      padding-right: 60px;
    }

    cursor: pointer;

    &::before {
      content: attr(data-hover);

      position: absolute;
      top: 0;
      left: 0;
      width: 0%;
      overflow: hidden;
      white-space: nowrap;
      color: $color-blue;
      transition: all 0.5s cubic-bezier(.17,.67,.32,.87);
    }

    &::after {
      content: attr(data-state);

      position: absolute;
      top: 18%;
      right: 15px;

      @include mq(sm) {
        top: 20%;
        right: 40px;
      }

      opacity: 1;

      font-size: 0.75rem;
      font-weight: normal;
      color: $color-light;
      -webkit-text-stroke: 0;
    }
    
    @at-root .pointerevents:not(.touchevents) &:hover::before,
    .no-pointerevents.no-touchevents &:hover::before {
      width: 100%;
    }

    &[data-state="nothover"] {
      pointer-events: none;

      &::after {
        display: none;
      }
    }

    &[data-hover="World"] {
      -webkit-text-stroke: 2px $color-light;

      &::before {
        color: $color-light;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/SliderTest.vue